import { useEffect, useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { uploadImages } from '@/api/upload';

const MAX_IMAGES = 5;
const MAX_SIZE = 10 * 1024 * 1024;

interface PreviewItem {
  id: string;
  file: File;
  url: string;
}

interface SignReceiptModalProps {
  isOpen: boolean;
  orderId: string;
  /** sign=本人签收，proxy=同科室代收 */
  mode?: 'sign' | 'proxy';
  submitting?: boolean;
  onCancel: () => void;
  /** 上传成功后回传图片 URL 列表 */
  onConfirm: (imageUrls: string[]) => void | Promise<void>;
}

export default function SignReceiptModal({
  isOpen,
  orderId,
  mode = 'sign',
  submitting = false,
  onCancel,
  onConfirm,
}: SignReceiptModalProps) {
  const [items, setItems] = useState<PreviewItem[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setItems((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.url));
      return [];
    });
    setError(null);
    setUploading(false);
    setDragOver(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [isOpen]);

  if (!isOpen) return null;

  const isProxy = mode === 'proxy';
  const title = isProxy ? '代收确认' : '签收确认';
  const tip = isProxy
    ? '您正在代收本科室其他同事的订单，请上传签收凭据图片（最多 5 张）：'
    : '请上传签收凭据图片以完成签收确认（最多 5 张）：';
  const confirmLabel = isProxy ? '确认代收' : '确认签收';
  const busy = uploading || submitting;
  const remain = MAX_IMAGES - items.length;

  const appendFiles = (fileList: FileList | File[]) => {
    const incoming = Array.from(fileList);
    if (incoming.length === 0) return;

    setError(null);
    if (remain <= 0) {
      setError(`最多上传 ${MAX_IMAGES} 张图片`);
      return;
    }

    const next: PreviewItem[] = [];
    let rejectReason = '';

    for (const file of incoming) {
      if (next.length >= remain) {
        rejectReason = `最多上传 ${MAX_IMAGES} 张图片，已自动截取前 ${MAX_IMAGES} 张`;
        break;
      }
      if (!file.type.startsWith('image/')) {
        rejectReason = '仅支持图片格式（JPG、PNG、WEBP等）';
        continue;
      }
      if (file.size > MAX_SIZE) {
        rejectReason = '单张图片不能超过 10MB';
        continue;
      }
      next.push({
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        url: URL.createObjectURL(file),
      });
    }

    if (next.length > 0) {
      setItems((prev) => [...prev, ...next]);
    }
    if (rejectReason) setError(rejectReason);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) appendFiles(e.target.files);
    e.target.value = '';
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files) appendFiles(e.dataTransfer.files);
  };

  const handleRemove = (id: string) => {
    setItems((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target) URL.revokeObjectURL(target.url);
      return prev.filter((p) => p.id !== id);
    });
    setError(null);
  };

  const handleConfirm = async () => {
    if (items.length === 0) {
      setError('请至少上传一张签收凭据图片');
      return;
    }
    if (busy) return;

    setUploading(true);
    setError(null);
    try {
      const res = await uploadImages(items.map((i) => i.file));
      const ok = Number(res.errno) === 0 || res.error === false || Number(res.error) === 0;
      const urls = Array.isArray(res.data) ? res.data.filter(Boolean) : [];
      if (!ok || urls.length === 0) {
        setError(res.message || res.msg || '图片上传失败，请重试');
        return;
      }
      await onConfirm(urls.slice(0, MAX_IMAGES));
    } catch {
      setError('图片上传失败，请稍后重试');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40" onClick={busy ? undefined : onCancel}>
      <div
        className="bg-background-50 rounded-xl w-full max-w-md mx-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-background-200 flex items-center gap-3">
          <div
            className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
              isProxy ? 'bg-secondary-100' : 'bg-primary-100'
            }`}
          >
            <i
              className={`${isProxy ? 'ri-user-received-line text-secondary-600' : 'ri-check-double-line text-primary-600'} text-lg`}
            />
          </div>
          <h3 className="text-sm font-semibold text-foreground-900">{title}</h3>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="bg-background-100 rounded-lg p-3">
            <p className="text-xs text-foreground-500">订单号</p>
            <p className="text-sm font-medium text-foreground-800 mt-0.5">{orderId}</p>
          </div>

          <p className="text-sm text-foreground-600">{tip}</p>

          {items.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="relative aspect-square rounded-lg overflow-hidden border border-background-200 bg-background-100"
                >
                  <img src={item.url} alt="签收凭据预览" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => handleRemove(item.id)}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-background-50 transition-colors disabled:opacity-50"
                  >
                    <i className="ri-close-line text-xs" />
                  </button>
                </div>
              ))}
              {remain > 0 && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => fileInputRef.current?.click()}
                  className="aspect-square rounded-lg border-2 border-dashed border-background-300 hover:border-primary-300 hover:bg-background-50 flex flex-col items-center justify-center gap-1 text-foreground-400 transition-colors disabled:opacity-50"
                >
                  <i className="ri-add-line text-lg" />
                  <span className="text-[10px]">继续添加</span>
                </button>
              )}
            </div>
          )}

          {items.length === 0 && (
            <div
              onDrop={handleDrop}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                dragOver
                  ? 'border-primary-400 bg-primary-50'
                  : 'border-background-300 hover:border-primary-300 hover:bg-background-50'
              }`}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="w-12 h-12 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-3">
                <i className="ri-image-add-line text-xl text-foreground-400" />
              </div>
              <p className="text-sm text-foreground-600 mb-1">拖拽图片到此处，或点击上传</p>
              <p className="text-xs text-foreground-400">支持一次多选，最多 {MAX_IMAGES} 张，单张不超过 10MB</p>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileChange}
            className="hidden"
          />

          {items.length > 0 && (
            <p className="text-xs text-foreground-400">
              已选 {items.length}/{MAX_IMAGES} 张
              {remain > 0 ? '，可继续添加' : ''}
            </p>
          )}

          {error && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-50 text-primary-700 text-sm">
              <i className="ri-error-warning-line shrink-0" />
              {error}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-background-200 flex items-center justify-end gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="px-4 py-2 rounded-lg border border-background-300 text-foreground-600 text-sm hover:bg-background-100 transition-colors whitespace-nowrap disabled:opacity-50"
          >
            取消
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleConfirm()}
            className={`px-4 py-2 rounded-lg text-background-50 text-sm font-medium transition-colors whitespace-nowrap inline-flex items-center gap-1.5 disabled:opacity-60 ${
              isProxy
                ? 'bg-secondary-500 hover:bg-secondary-600'
                : 'bg-primary-500 hover:bg-primary-600'
            }`}
          >
            {busy && <i className="ri-loader-4-line animate-spin text-sm" />}
            {uploading ? '上传中...' : submitting ? '提交中...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
